import React from 'react';
import { ChevronDown, ChevronRight, Database, Users } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../../components/ui/collapsible";
import { Badge } from "../../../../components/ui/badge";
import { Progress } from "../../../../components/ui/progress";
import { PodMetricsMap, NodeMetricsMap } from '../utils/types';
import { bytesToGB, parseMemory } from '../utils/helpers';
import PodTable from './PodTable';

interface NodeItemProps {
  nodeName: string;
  nodeData: {
    nodeInfo: any;
    pods: any[];
    nodeType: 'gateway' | 'normal';
  };
  nodeMetrics: NodeMetricsMap;
  podMetrics: PodMetricsMap;
  podSessionMap: Map<string, any[]>;
  isLoadingSessions: boolean;
  isExpanded: boolean;
  onToggleExpand: (nodeName: string) => void;
}

const NodeItem: React.FC<NodeItemProps> = ({
  nodeName,
  nodeData,
  nodeMetrics,
  podMetrics,
  podSessionMap,
  isLoadingSessions,
  isExpanded,
  onToggleExpand
}) => {
  const nodeMetric = nodeMetrics[nodeName];
  const memoryUsageGB = nodeMetric ? bytesToGB(parseMemory(nodeMetric.memory.usage)) : '0';
  const memoryCapacityGB = nodeMetric ? bytesToGB(parseMemory(nodeMetric.memory.capacity)) : '0';
  
  // Calculate total sessions on this node
  const sessionCount = React.useMemo(() => {
    let totalSessions = 0;
    
    if (!isLoadingSessions) {
      // Count pods with sessions
      nodeData.pods.forEach(pod => {
        const sessions = podSessionMap.get(pod.metadata.name) || [];
        if (sessions.length > 0) {
          totalSessions++;
        }
      });
    }
    
    return totalSessions;
  }, [nodeData.pods, podSessionMap, isLoadingSessions]);

  // Calculate node utilization (sessions / pods ratio)
  const utilizationPercentage = nodeData.pods.length > 0 
    ? Math.round((sessionCount / nodeData.pods.length) * 100) 
    : 0;

  return (
    <Collapsible 
      key={nodeName}
      open={isExpanded}
      onOpenChange={() => onToggleExpand(nodeName)}
      className="border rounded-md"
    >
      <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
        <div className="flex items-center">
          {isExpanded ? 
            <ChevronDown className="h-4 w-4 mr-2" /> : 
            <ChevronRight className="h-4 w-4 mr-2" />
          }
          <span className="font-medium">{nodeName}</span>
          <Badge variant="outline" className="ml-3">
            {nodeData.pods.length} pods
          </Badge>
          {!isLoadingSessions && (
            <Badge variant="secondary" className="ml-2 flex items-center">
              <Users className="h-3 w-3 mr-1" />
              {sessionCount} sessions ({utilizationPercentage}%)
            </Badge>
          )}
          <Badge variant={nodeData.nodeType === 'gateway' ? 'secondary' : 'outline'} className="ml-2">
            {nodeData.nodeType === 'gateway' ? 'Gateway' : 'Normal'}
          </Badge>
        </div>
        <div className="flex items-center space-x-2">
          {nodeData.nodeInfo?.status?.capacity && (
            <>
              <Badge variant="secondary">
                CPU: {nodeData.nodeInfo.status.capacity.cpu || 'N/A'}
              </Badge>
              <Badge variant="secondary">
                Memory: {memoryUsageGB} GB / {memoryCapacityGB} GB
              </Badge>
              {nodeMetric && (
                <Badge variant="secondary" className="flex items-center">
                  <Database className="h-3 w-3 mr-1" />
                  {nodeMetric.memory.usagePercentage.toFixed(1)}%
                </Badge>
              )}
            </>
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4">
        {/* Memory utilization bar */}
        {nodeMetric && (
          <div className="mb-4 mt-0">
            <div className="flex justify-between text-xs mb-1">
              <span>Memory: {memoryUsageGB} GB</span>
              <span>Total: {memoryCapacityGB} GB</span>
            </div>
            <Progress 
              value={nodeMetric.memory.usagePercentage} 
              className="h-2"
            />
          </div>
        )}
        
        {/* Session/Pod utilization bar */}
        {!isLoadingSessions && (
          <div className="mb-4 mt-2">
            <div className="flex justify-between text-xs mb-1">
              <span>Sessions: {sessionCount}</span>
              <span>Pod utilization: {utilizationPercentage}%</span>
            </div>
            <Progress 
              value={utilizationPercentage} 
              className="h-2"
            />
          </div>
        )}
        
        {/* Pod table */}
        <PodTable 
          pods={nodeData.pods}
          podMetrics={podMetrics}
          podSessionMap={podSessionMap}
          isLoadingSessions={isLoadingSessions}
          showDeployment={true}
          showNode={false}
          showApp={true}
          showCpu={false}
          showMemoryInfo={false}
          emptyMessage="No pods running on this node."
          showSearch={nodeData.pods.length > 10}
        />
      </CollapsibleContent>
    </Collapsible>
  );
};

export default NodeItem; 